import { Injectable } from "@angular/core";
import { IndexedDBService } from "../indexed-db/indexed-db.service";
import { BehaviorSubject, combineLatestWith, from, lastValueFrom, Observable, of, tap, zip } from "rxjs";
import { FileUploadService } from "../file-upload/file-upload.service";
import { MediaApiService } from "src/app/api/bizzmine/media/media-api.service";
import { MediaCommitDto } from "src/models/ts/media-commit-dto.model";
import { mediaCommitDtoMapper } from "src/app/features/bizzmine/form/components/controls/form-mailing-list/mailing-attachments-upload/functions/media-commit-dto-mapper";

export enum PwaOfflineRequestStatus {
	UNDEFINED = 0,
	COMPLETED = 1,
	ERROR = 2
}
export interface MediaCacheData {
	files: Array<File>;
	formId: string;
	collectionFormId: number,
	collectionId: number,
	formFieldId: number,
	instanceId: number,
	versionId: number,
	questionId: number,
	chunkSize: number;
	maxConcurrentUploads: number | null;
//	status?: PwaOfflineRequestStatus;
	filesMetaData?: Array<{
		id: string;
		status: PwaOfflineRequestStatus;
	}>
}

@Injectable({
	providedIn: "root",
})

export class MediaService {

	public constructor(
		private indexedDbService: IndexedDBService,
		private fileUploadService: FileUploadService,
		private mediaApiService: MediaApiService
	) {}

	public async addFilesToCache(data: MediaCacheData): Promise<void> {
		//check if the id already exists in the indexeddb
		const id = `${data.collectionFormId}_${data.collectionId}_${data.formFieldId}_${data.instanceId}_${data.versionId}_${data.questionId}`;
		const existingData = await this.indexedDbService.getData("media", id) as { id: string; data: MediaCacheData } | undefined;
		
		if (existingData) {
			data.files = data.files.concat(existingData.data.files);
		}
		data.filesMetaData = data.files.map(() => {return {
			id: crypto.randomUUID(),
			status: PwaOfflineRequestStatus.UNDEFINED,
		}});

		return this.indexedDbService.saveData("media", {
			id: id,
			data: data,
		});
	}

	public processMediaUploadQueue(): Promise<[number, { index: number; key: string; fileSize: number; fileName: string }][]> {
		return new Promise((resolve, reject) => {
			//commit files for each file if OK set status OK -> update record in db
			this.indexedDbService.getStore("media").then((data: { id: string; data: MediaCacheData }[]) => {
				const uploadRequests: Observable<
				[res: number, { index: number; key: string; fileSize: number; fileName: string }]
				>[] = [];
				//filter all records status unhandled Status 0
				//data = data.filter((item) => item.data.status === PwaOfflineRequestStatus.UNDEFINED);
				
				if(data.length){
					data.forEach((item) => {
						
						// only  upload undefined after error?
						
						for (let i = 0; i < item.data.files.length; i++) {
							const uploadRequest = from(
								this.uploadFile(item.data.files[i], item.data.chunkSize, item.data.maxConcurrentUploads)
							).pipe(
								combineLatestWith(
									of({
										index: i,
										key: item.id,
										fileSize: item.data.files[i].size,
										fileName: item.data.files[i].name,
									})
								),
								tap(() => {
									// if completed set state completed item.data.files[i].sta
									this.indexedDbService.saveData("media", {
										id: item.id,
										data: {
											...item.data,
											status: PwaOfflineRequestStatus.COMPLETED,
										},
									}).then(() => {
									})
								}),
							);
							uploadRequests.push(uploadRequest);
						}
					});

					zip(uploadRequests).subscribe({
						next: (response) => {
							resolve(response);
						},
						error: (error) => {
							console.error("Error uploading files:", error);
							reject(error);
						},
					});
				}else{
					resolve([]);
				}

			});
		});
	}

	private uploadFile(file: File, chunkSize: number, maxConcurrentUploads: number | null): Promise<number> {
		return new Promise((resolve, reject) => {
			const fileUploads = this.fileUploadService.uploadFiles({
				preValidation$: (data) => of(data),
				upload$: (formData) => this.mediaApiService.uploadFileFromFormData(formData),
				files: [file],
				chunkSize: chunkSize,
				maxConcurrentUploads: maxConcurrentUploads,
			});

			fileUploads.forEach((fileUpload) => {
				fileUpload.onError = (): void => {
					console.error("onError", fileUpload);
					//set status for current file
					reject();
				};
				fileUpload.onCompleted = (): void => {
					if (fileUpload.fileChunkUpload) {
						lastValueFrom(this.mediaApiService.commitMailAttachment(mediaCommitDtoMapper(fileUpload.fileId, fileUpload.fileChunkUpload))).then((response) => {
							resolve(response);
						});
					}
				};
			});
		});
	}

	public getTotalPendingRequests(): Promise<number> {
		return this.indexedDbService.getStore("media").then((data: { id: string; data: { url: string; payload: any } }[]) => {
			return data.length;
		});
	}

	// get attachment by url and remove from indexeddb
	public removeAttachmentByUrl(data: Partial<MediaCacheData>, attachmentId: number): Promise<{ id: string; data: { url: string; payload: any } } | undefined> {
		const id = `${data.collectionFormId}_${data.collectionId}_${data.formFieldId}_${data.instanceId}_${data.versionId}_${data.questionId}`;

		return this.indexedDbService.getStore("media").then((data: { id: string; data: { url: string; payload: any } }[]) => {
			const mediaRow = data.find((item) => item.id === id);
			if (!mediaRow) {
				return;
			}
			
			//check if multiple attachments
			//if only 1 attachment delete row else update row
			if (mediaRow && mediaRow.data.payload.Attachments.length === 1) {
				this.indexedDbService.deleteData("media", mediaRow.id);
			} else if (mediaRow) {
				mediaRow.data.payload.Attachments = mediaRow.data.payload.Attachments.filter((item: { Id: number }) => item.Id !== attachmentId);
				this.indexedDbService.saveData("media", mediaRow);
			}
			return mediaRow;
		});
	}

	
}
